import React, { useState, useEffect } from 'react';
import { Printer, Download, Usb, RefreshCw, Check, AlertCircle, Settings, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as printerApi from '@/services/printerApi';
import PrintMethodToggle from '@/components/print/PrintMethodToggle';

/**
 * Reusable Print Dialog Component
 * 
 * Props:
 * - open: boolean - Dialog open state
 * - onOpenChange: (open: boolean) => void - Dialog open state handler
 * - codes: string[] - Array of codes to print
 * - sortNos: string[] - Optional array of Sort Nos for each code
 * - labelType: 'usn' | 'bundle' | 'document' - Type of labels (affects size)
 * - title: string - Dialog title
 */
const PrintDialog = ({
  open,
  onOpenChange,
  codes = [],
  sortNos = null,
  extraTexts = null,
  labelType = 'usn',
  defaultConfigName = null,
  defaultSection = null,
  operationKey = 'print-dialog',
  title = 'Print Labels',
  onPrinted = null
}) => {
  const [printerLanguage, setPrinterLanguage] = useState('tspl');
  const [copies, setCopies] = useState(1);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printResult, setPrintResult] = useState(null);
  // Idempotency token for THIS print action. Generated once when the dialog
  // opens; every "Print" click within the same open dialog (incl. a retry
  // after a false "Network Time Out") reuses it so the backend won't enqueue
  // a duplicate physical print. Reset when the dialog re-opens / codes change.
  const [printToken, setPrintToken] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [showCommands, setShowCommands] = useState(false);
  const [commands, setCommands] = useState('');
  const [usbSupported, setUsbSupported] = useState(false);
  
  // Label config state
  const [labelConfigs, setLabelConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);

  // Label size based on type (fallback if no config selected)
  const labelSizes = {
    usn: { width: 25, height: 25, name: 'USN Sticker' },
    bundle: { width: 50, height: 35, name: 'Bundle Sticker (50mm x 35mm)' },
    document: { width: 60, height: 40, name: 'Document Label (60mm x 40mm)' }
  };

  // Resolve live label size: prefer selected config, else labelType default
  const selectedConfig = labelConfigs.find(c => c.config_id === selectedConfigId);
  const currentLabelSize = selectedConfig
    ? {
        width: selectedConfig.label_width_mm,
        height: selectedConfig.label_height_mm,
        name: `${selectedConfig.config_name} (${selectedConfig.label_width_mm}mm x ${selectedConfig.label_height_mm}mm)`
      }
    : (labelSizes[labelType] || labelSizes.usn);

  useEffect(() => {
    setUsbSupported(printerApi.isWebUSBSupported());
    if (labelType === 'usn') {
      loadLabelConfigs();
    }
  }, [labelType, defaultConfigName, defaultSection]);

  useEffect(() => {
    if (open && codes.length > 0 && codes.length <= 10) {
      loadPreview();
    }
  }, [open, codes]);

  // A new print action → a fresh idempotency token (retries within the same
  // open dialog reuse it; a re-open / new codes starts a new one).
  useEffect(() => {
    if (open) {
      const t = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `pt_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      setPrintToken(t);
    }
  }, [open, codes, copies]);

  const loadLabelConfigs = async () => {
    setIsLoadingConfigs(true);
    try {
      const response = await apiClient.get('/label-configs/list');
      const configs = response.data.configs || [];
      setLabelConfigs(configs);

      // Priority: (1) match by defaultSection, (2) defaultConfigName, (3) is_default flag
      let selected = null;
      if (defaultSection) {
        selected = configs.find(c => (c.section || '').toLowerCase() === defaultSection.toLowerCase());
      }
      if (!selected && defaultConfigName) {
        selected = configs.find(c => c.config_name === defaultConfigName);
      }
      if (!selected) {
        selected = configs.find(c => c.is_default);
      }
      if (selected) {
        setSelectedConfigId(selected.config_id);
      }
    } catch (error) {
      console.error('Error loading label configs:', error);
    } finally {
      setIsLoadingConfigs(false);
    }
  };

  const loadPreview = async () => {
    setIsLoadingPreview(true);
    try {
      const result = await printerApi.generatePrintPreview(
        codes.slice(0, 10),
        currentLabelSize.width,
        currentLabelSize.height
      );
      setPreviewData(result.previews || []);
    } catch (error) {
      console.error('Error loading preview:', error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const generateCommands = async () => {
    setIsPrinting(true);
    try {
      let result;
      // Use USN labels API if sortNos are provided
      if (labelType === 'usn' && sortNos) {
        result = await printerApi.generateUSNLabels(codes, printerLanguage, copies, sortNos, selectedConfigId);
      } else {
        result = await printerApi.generatePrintCommands(
          codes,
          printerLanguage,
          copies,
          currentLabelSize.width,
          currentLabelSize.height,
          extraTexts
        );
      }
      setCommands(result.commands);
      setShowCommands(true);
      setPrintResult(result);
    } catch (error) {
      console.error('Error generating commands:', error);
      toast.error('Failed to generate print commands');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleDownload = async () => {
    setIsPrinting(true);
    try {
      // Generate commands first
      let result;
      if (labelType === 'usn' && sortNos) {
        result = await printerApi.generateUSNLabels(codes, printerLanguage, copies, sortNos, selectedConfigId);
      } else {
        result = await printerApi.generatePrintCommands(
          codes,
          printerLanguage,
          copies,
          currentLabelSize.width,
          currentLabelSize.height,
          extraTexts
        );
      }
      
      // Create download
      const extMap = { zpl: 'zpl', tspl: 'prn', escpos: 'bin' };
      const ext = extMap[printerLanguage] || 'txt';
      const filename = `usn_labels_${codes.length}.${ext}`;
      
      const blob = new Blob([result.commands], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Print file downloaded');
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Failed to download print file');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleUSBPrint = async () => {
    setIsPrinting(true);
    try {
      // Generate commands first
      let result;
      if (labelType === 'usn' && sortNos) {
        result = await printerApi.generateUSNLabels(codes, printerLanguage, copies, sortNos, selectedConfigId);
      } else {
        result = await printerApi.generatePrintCommands(
          codes,
          printerLanguage,
          copies,
          currentLabelSize.width,
          currentLabelSize.height,
          extraTexts
        );
      }
      
      // Send to the active destination (USB, or network when this screen's switch is set)
      const device = await printerApi.requestUSBPrinter();
      await printerApi.sendToUSBPrinter(device, result.commands, { clientToken: printToken });
      toast.success(`Sent ${codes.length} label(s) to printer`);
      setPrintResult({ success: true, method: 'usb' });
      if (typeof onPrinted === 'function') { try { onPrinted(); } catch (e) { /* non-fatal */ } }
    } catch (error) {
      console.error('Error printing via USB:', error);
      toast.error(error.message || 'Failed to print via USB');
    } finally {
      setIsPrinting(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(commands);
    toast.success('Commands copied to clipboard');
  };

  const handleClose = () => {
    setPrintResult(null);
    setShowCommands(false);
    setCommands('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {codes.length} label{codes.length !== 1 ? 's' : ''} to print • {currentLabelSize.name}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <Tabs defaultValue="settings" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="settings">
                <Settings className="w-4 h-4 mr-1" />
                Settings
              </TabsTrigger>
              <TabsTrigger value="preview">
                <Eye className="w-4 h-4 mr-1" />
                Preview
              </TabsTrigger>
              <TabsTrigger value="commands" disabled={!showCommands}>
                Commands
              </TabsTrigger>
            </TabsList>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-4 mt-4">
              {/* Label Config Selector (USN labels only) */}
              {labelType === 'usn' && labelConfigs.length > 0 && (
                <div>
                  <Label className="text-sm text-gray-600">Label Configuration</Label>
                  <Select value={selectedConfigId || 'system_default'} onValueChange={(value) => setSelectedConfigId(value === 'system_default' ? '' : value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Use system default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="system_default">System Default</SelectItem>
                      {labelConfigs.map((config) => (
                        <SelectItem key={config.config_id} value={config.config_id}>
                          {config.config_name}
                          {config.is_default && ' (Default)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select a custom configuration or use system default
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">Printer Language</Label>
                  <Select value={printerLanguage} onValueChange={setPrinterLanguage}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tspl">TSPL (TSC Printers)</SelectItem>
                      <SelectItem value="zpl">ZPL (Zebra & Compatible)</SelectItem>
                      <SelectItem value="escpos">ESC/POS (Receipt Printers)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Copies per Label</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={copies}
                    onChange={(e) => setCopies(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-2">Label Information</div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Type:</span>
                    <span className="ml-2 font-medium capitalize">{labelType}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Size:</span>
                    <span className="ml-2 font-medium">{currentLabelSize.width}mm x {currentLabelSize.height}mm</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Total Labels:</span>
                    <span className="ml-2 font-medium">{codes.length * copies}</span>
                  </div>
                </div>
              </div>

              {/* Print Methods */}
              <div className="space-y-3">
                <Label className="text-sm text-gray-600">Print Destination</Label>

                <PrintMethodToggle screenKey={operationKey} className="mb-1" />

                <Button
                  onClick={handleUSBPrint}
                  disabled={isPrinting || codes.length === 0}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="usb-print-btn"
                >
                  {isPrinting ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Usb className="w-4 h-4 mr-2" />
                  )}
                  Print
                </Button>

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={generateCommands}
                    disabled={isPrinting || codes.length === 0}
                    variant="outline"
                    data-testid="generate-commands-btn"
                  >
                    {isPrinting ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Printer className="w-4 h-4 mr-2" />
                    )}
                    Generate Commands
                  </Button>
                  <Button
                    onClick={handleDownload}
                    disabled={isPrinting || codes.length === 0}
                    variant="outline"
                    data-testid="download-btn"
                  >
                    {isPrinting ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download File
                  </Button>
                </div>

                {!usbSupported && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    USB printing not available in this browser. Use Chrome or Edge for direct USB printing.
                  </p>
                )}
              </div>

              {/* Success Message */}
              {printResult?.success && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="text-green-700">
                    {printResult.method === 'usb' 
                      ? 'Labels sent to printer!' 
                      : 'Commands generated successfully!'}
                  </span>
                </div>
              )}
            </TabsContent>

            {/* Preview Tab */}
            <TabsContent value="preview" className="mt-4">
              {isLoadingPreview ? (
                <div className="text-center py-8 text-gray-500">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading preview...
                </div>
              ) : previewData.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {previewData.map((preview, idx) => (
                    <div 
                      key={idx} 
                      className="border border-gray-200 rounded-lg p-3 bg-white"
                      style={{ 
                        aspectRatio: `${currentLabelSize.width}/${currentLabelSize.height}` 
                      }}
                    >
                      <div className="flex items-center gap-2 h-full">
                        <img 
                          src={preview.qr_image} 
                          alt={preview.code}
                          className="w-16 h-16 object-contain"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-mono truncate" title={preview.code}>
                            {preview.code}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {codes.length > 10 && (
                    <div className="border border-dashed border-gray-300 rounded-lg p-3 flex items-center justify-center text-gray-500 text-sm">
                      +{codes.length - 10} more
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No preview available
                </div>
              )}
            </TabsContent>

            {/* Commands Tab */}
            <TabsContent value="commands" className="mt-4">
              {commands ? (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      {printerLanguage.toUpperCase()} Commands
                    </span>
                    <Button onClick={copyToClipboard} variant="ghost" size="sm">
                      Copy to Clipboard
                    </Button>
                  </div>
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-auto max-h-60 font-mono">
                    {commands}
                  </pre>
                  <p className="text-xs text-gray-500">
                    Send these commands to your printer via a terminal utility or printer software.
                  </p>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Generate commands first
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button onClick={handleClose} variant="secondary">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrintDialog;
